import type React from 'react';
import { AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';

export interface PageErrorProps {
  /** Error title */
  title?: string;
  /** Error message to display */
  message: string;
  /** Optional callback for retry action */
  onRetry?: () => void;
  /** Optional callback for back/navigation action */
  onBack?: () => void;
  /** Label for the back button */
  backLabel?: string;
  /** Variant: 'default' for card display, 'inline' for compact display */
  variant?: 'default' | 'inline';
  /** Additional className */
  className?: string;
}

/**
 * PageError Component
 *
 * Displays a branded error state with:
 * - Editorial-styled alert icon with warm colors
 * - Optional retry and back buttons
 * - Card-based layout matching the app aesthetic
 *
 * @example
 * // Basic error
 * <PageError message="Failed to load articles" />
 *
 * @example
 * // Error with actions
 * <PageError
 *   title="Connection Error"
 *   message="Unable to reach the server. Please try again."
 *   onRetry={() => refetch()}
 *   onBack={() => navigate('/dashboard')}
 * />
 *
 * @example
 * // Inline error (compact)
 * <PageError variant="inline" message="Something went wrong" />
 */
export const PageError: React.FC<PageErrorProps> = ({
  title = 'Error',
  message,
  onRetry,
  onBack,
  backLabel = 'Go Back',
  variant = 'default',
  className,
}) => {
  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2 text-destructive', className)}>
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="text-sm">{message}</span>
        {onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            className="ml-2 h-7 px-2"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn('bg-paper-light min-h-full', className)}>
      {/* Editorial header for error state */}
      <div className="border-b border-ochre/10 bg-paper-warm/30">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-8 bg-ochre" />
            <div className="w-1.5 h-1.5 rounded-full bg-burnt-sienna" />
          </div>
          <h1
            className="text-3xl font-bold text-charcoal tracking-tight"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {title}
          </h1>
          <p className="text-muted-warm mt-1 text-sm">
            Something went wrong
          </p>
        </div>
      </div>

      {/* Error content */}
      <div className="p-6 container mx-auto max-w-6xl">
        <Card className="paper-edge bg-paper-warm/10 border-destructive/30">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center py-8 space-y-6">
              {/* Error icon with editorial styling */}
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>

              {/* Error message */}
              <div className="space-y-2 max-w-md">
                <h3
                  className="text-xl font-semibold text-charcoal"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {title}
                </h3>
                <p className="text-muted-warm">{message}</p>
              </div>

              {/* Action buttons */}
              {(onRetry || onBack) && (
                <div className="flex items-center gap-3">
                  {onBack && (
                    <Button
                      variant="outline"
                      onClick={onBack}
                      className="border-ochre/30 text-charcoal hover:bg-ochre/10"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      {backLabel}
                    </Button>
                  )}
                  {onRetry && (
                    <Button
                      onClick={onRetry}
                      className="bg-burnt-sienna hover:bg-ochre text-white"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Try Again
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/**
 * PageErrorCard Component
 *
 * A simpler error card for use within existing page layouts.
 * Does not include the full page wrapper or editorial header.
 *
 * @example
 * <PageLayout title="Dashboard">
 *   {error ? (
 *     <PageErrorCard
 *       message={error.message}
 *       onRetry={() => refetch()}
 *     />
 *   ) : (
 *     <Content />
 *   )}
 * </PageLayout>
 */
export const PageErrorCard: React.FC<Omit<PageErrorProps, 'variant'>> = ({
  title = 'Error',
  message,
  onRetry,
  onBack,
  backLabel = 'Go Back',
  className,
}) => (
  <Card className={cn('paper-edge bg-paper-warm/10 border-destructive/30', className)}>
    <CardContent className="pt-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
          <AlertCircle className="h-5 w-5 text-destructive" />
        </div>
        <div className="flex-1 space-y-4">
          <div>
            <h3 className="font-semibold text-charcoal">{title}</h3>
            <p className="text-sm text-muted-warm mt-1">{message}</p>
          </div>
          {(onRetry || onBack) && (
            <div className="flex items-center gap-2">
              {onBack && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onBack}
                  className="border-ochre/30 text-charcoal hover:bg-ochre/10"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {backLabel}
                </Button>
              )}
              {onRetry && (
                <Button
                  size="sm"
                  onClick={onRetry}
                  className="bg-burnt-sienna hover:bg-ochre text-white"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);
